import {
    extractActiveFiltersFromQueryParams,
    FilterConfig,
    FilterFieldOptions,
    OMNIBOX_SEARCH_PARAM_NAME,
    useQuery,
} from './OperatorFilterHelpers.tsx';
import { useEffect, useState } from 'react';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { CustomerSearchResult } from '../../parkingabo-users/OperatorParkingaboUsers.tsx';
import { ProductSearchResult } from '../../products/OperatorProducts.tsx';
import {
    BackendFilterConfig,
    BackendFilterType,
    BackendFilterValueConfig,
} from './OperatorFilterBackendConfiguration.tsx';
import { CheckinStatisticsResponse } from '../../statistics/OperatorCheckinStatisticsRoute.tsx';
import { OffstreetTransactionsSearchResult } from '../../offstreet-transactions/OperatorOffstreetTransactionsRoute.tsx';
import { PeripheryHistorySearchResult } from '../../periphery-history/PeripheryHistory.tsx';
import { PermitStatisticsResponse } from '../../statistics/OperatorPermitStatisticsRoute.tsx';
import { RecodeStatementsResponse } from '../../cloud-connector/CloudConnectorRecodeStatements.tsx';
import { OnstreetTransactionsSearchResult } from '../../onstreet-transactions/OperatorOnstreetTransactionList.tsx';
import { RecodeQrCodesResponse } from '../../cloud-connector/CloudConnectorRecodeQrCodes.tsx';
import { normalizeScannedSearchInput } from './OperatorSearchInputNormalization.ts';
import { EnforcementLogSearchResult } from '../../enforcement-log/EnforcementLogList.tsx';

export function useProductFilterConfiguration() {
    return useFilterConfiguration<ProductSearchResult>(
        '/ui-api/operator-account/product/filter-configuration',
        `/ui-api/operator-account/product/search`,
    );
}

export function useGuestProductFilterConfiguration() {
    return useFilterConfiguration<ProductSearchResult>(
        '/ui-api/operator-account/guest-product/filter-configuration',
        `/ui-api/operator-account/guest-product/search`,
    );
}

export function useOnstreetTransactionsFilterConfiguration() {
    return useFilterConfiguration<OnstreetTransactionsSearchResult>(
        '/ui-api/operator-account/onstreet-transactions/filter-configuration',
        `/ui-api/operator-account/onstreet-transactions/search`,
    );
}

export function useOffstreetTransactionsFilterConfiguration() {
    return useFilterConfiguration<OffstreetTransactionsSearchResult>(
        '/ui-api/operator-account/offstreet-transactions/filter-configuration',
        `/ui-api/operator-account/offstreet-transactions/search`,
    );
}

export function useParkingaboUserFilterConfiguration() {
    return useFilterConfiguration<CustomerSearchResult>(
        '/ui-api/operator-account/parkingabo/customer/filter-configuration',
        `/ui-api/operator-account/parkingabo/customer/search`,
    );
}

export function useCloudConnectorPeripheryHistoryFilterConfiguration() {
    return useFilterConfiguration<PeripheryHistorySearchResult>(
        '/ui-api/operator-account/periphery-history/filter-configuration',
        `/ui-api/operator-account/periphery-history/search`,
    );
}

export function useEnforcementLogFilterConfiguration() {
    return useFilterConfiguration<EnforcementLogSearchResult>(
        '/ui-api/operator-account/enforcement-log/filter-configuration',
        `/ui-api/operator-account/enforcement-log/search`,
    );
}

export type FilteredSearchResult =
    | ProductSearchResult
    | CustomerSearchResult
    | PeripheryHistorySearchResult
    | OnstreetTransactionsSearchResult
    | OffstreetTransactionsSearchResult
    | EnforcementLogSearchResult;

export function useCheckinStatisticsFilterConfiguration() {
    return useFilterConfiguration<CheckinStatisticsResponse>(
        '/ui-api/operator-account/statistics/check-ins/filter-configuration',
        `/ui-api/operator-account/statistics/check-ins`,
    );
}

export function usePermitStatisticsFilterConfiguration() {
    return useFilterConfiguration<PermitStatisticsResponse>(
        '/ui-api/operator-account/statistics/permits/filter-configuration',
        `/ui-api/operator-account/statistics/permits`,
    );
}

export function useRecodeStatementsFilterConfiguration() {
    return useFilterConfiguration<RecodeStatementsResponse>(
        '/ui-api/operator-account/cloud-connector/recode/statements/filter-configuration',
        '/ui-api/operator-account/cloud-connector/recode/statements',
    );
}

export function useRecodeQrCodesFilterConfiguration() {
    return useFilterConfiguration<RecodeQrCodesResponse>(
        '/ui-api/operator-account/cloud-connector/recode/qr-codes/filter-configuration',
        '/ui-api/operator-account/cloud-connector/recode/qr-codes',
    );
}

function useFilterConfiguration<T>(filterConfigUrl: string, searchUrl: string) {
    const query = useQuery();
    const adjustedQueryString = buildAdjustedQueryString(query);
    const searchText = new URLSearchParams(adjustedQueryString).get(
        OMNIBOX_SEARCH_PARAM_NAME,
    );

    const [filterConfig, setFilterCofig] = useState<FilterConfig>({});
    const [filterConfigState] = useServerFetch<BackendFilterConfig[], object>(
        () => ({
            url: filterConfigUrl,
        }),
        {},
    );

    const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 450);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    const [debouncedQueryString, setDebouncedQueryString] = useState(
        () => adjustedQueryString,
    );
    useEffect(() => {
        if (searchText == debouncedSearchText) {
            const timeoutId = setTimeout(() => {
                setDebouncedQueryString(adjustedQueryString);
            }, 50);

            return () => clearTimeout(timeoutId);
        }
    }, [adjustedQueryString, debouncedSearchText, searchText]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (filterConfigState.status !== RequestStatus.SUCCESS) {
                setFilterCofig({});
                return;
            }
            setFilterCofig(buildFilterConfig(filterConfigState.data));
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [filterConfigState.data, filterConfigState.status]);

    const activeFilters = extractActiveFiltersFromQueryParams(
        query,
        filterConfig,
    );

    const [listState, refetchList] = useServerFetch<
        T,
        { queryString: string },
        null
    >(
        context => ({
            url: searchUrl + context.queryString,
        }),
        {
            queryString: '?' + debouncedQueryString,
        },
    );
    return {
        filterConfig: filterConfig,
        activeFilters: activeFilters,
        listState: listState,
        refetchList: refetchList,
        filterConfigState: filterConfigState,
        searchText: searchText,
    };
}

function buildAdjustedQueryString(query: URLSearchParams) {
    const queryCopy = new URLSearchParams(query);
    return normalizeSearchText(queryCopy).toString();
}

function normalizeSearchText(query: URLSearchParams): URLSearchParams {
    const searchText = query.get(OMNIBOX_SEARCH_PARAM_NAME);
    if (searchText) {
        const normalizedSearchText = normalizeScannedSearchInput(searchText);
        if (normalizedSearchText !== searchText) {
            query.set(OMNIBOX_SEARCH_PARAM_NAME, normalizedSearchText);
        }
    }
    return query;
}

function buildFilterConfig(filterConfigData: BackendFilterConfig[]) {
    const filters: FilterConfig = {};
    filterConfigData.forEach(filter => {
        switch (filter.type) {
            case BackendFilterType.DATE:
                filters[filter.key] = {
                    type: BackendFilterType.DATE,
                    displayName: filter.displayName,
                    disableSelectionInFuture: filter.disableSelectionInFuture,
                    value: null,
                };
                break;
            case BackendFilterType.SELECTION:
                filters[filter.key] = {
                    type: BackendFilterType.SELECTION,
                    displayName: filter.displayName,
                    values: filter.filters.reduce(
                        (
                            map: FilterFieldOptions,
                            filter: BackendFilterValueConfig,
                        ) => {
                            map[filter.value] = {
                                displayName: filter.displayName,
                                sortOrderNr: filter.sortOrderNr,
                            };
                            return map;
                        },
                        {},
                    ),
                };
                break;
            case BackendFilterType.DATE_RANGE:
                filters[filter.key] = {
                    type: BackendFilterType.DATE_RANGE,
                    displayName: filter.displayName,
                    disableSelectionInFuture: filter.disableSelectionInFuture,
                    fromValue: null,
                    toValue: null,
                };
                break;
            case BackendFilterType.ZONE:
                filters[filter.key] = {
                    type: BackendFilterType.ZONE,
                    displayName: filter.displayName,
                    filters: filter.filters.map(filter => ({
                        displayName: filter.displayName,
                        values: filter.zones.reduce(
                            (
                                map: FilterFieldOptions,
                                filter: BackendFilterValueConfig,
                            ) => {
                                map[filter.value as number] = {
                                    displayName: filter.displayName,
                                    sortOrderNr: filter.sortOrderNr,
                                };
                                return map;
                            },
                            {},
                        ),
                    })),
                };
                break;
        }
    });
    return filters;
}
